import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  DatePicker,
  Typography,
  Row,
  Col,
  Tag,
  message,
  Drawer,
  Spin,
  Alert,
  Skeleton,
  Progress,
  Select,
  Modal,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
  MessageOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { chatLogService } from '../../services/ChatLogService';
import PageHeader from '../Common/PageHeader';
import FilterCard from '../Common/FilterCard';
import StandardTable from '../Common/StandardTable';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { confirm } = Modal;

interface ChatLog {
  id: string;
  user_message: string;
  bot_response: string;
  timestamp: string;
  session_id?: string;
  user_id?: string;
  metadata?: Record<string, any>;
}

// Utility function for debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const History: React.FC = () => {
  const [chatLogs, setChatLogs] = useState<ChatLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [filterUserId, setFilterUserId] = useState<string | undefined>(undefined);
  
  // Drawer state
  const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
  const [selectedChatLog, setSelectedChatLog] = useState<ChatLog | null>(null);

  // Delete functionality state
  const [uniqueUsers, setUniqueUsers] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Debounce search text to prevent excessive API calls
  const debouncedSearchText = useDebounce(searchText, 500);

  // Memoized loadChatLogs function - simplified without component-level caching
  const loadChatLogs = useCallback(async (force = false) => {
    try {
      console.log(`🔄 Loading chat logs - Page: ${currentPage}, PageSize: ${pageSize}, Search: "${debouncedSearchText || 'none'}", Filter User ID: "${filterUserId || 'none'}"`);
      
      setLoading(true);
      setError(null);
      
      const response = await chatLogService.getChatLogs(
        currentPage,
        pageSize,
        debouncedSearchText || undefined,
        filterUserId
      );
      
      console.log(`✅ Received ${response.chatLogs.length} chat logs for page ${currentPage}`);
      
      setChatLogs(response.chatLogs);
      setTotal(response.total);
      
    } catch (error: any) {
      console.error('Load chat logs error:', error);
      setError(error.message || 'Failed to load chat logs');
      message.error(error.message || 'Failed to load chat logs');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchText, filterUserId]);



  // Load chat logs effect - separated from sessions loading
  useEffect(() => {
    loadChatLogs();
  }, [loadChatLogs]);

  // Load unique users for filter dropdown
  const loadUniqueUsers = useCallback(async () => {
    try {
      const users = await chatLogService.getUniqueUsers();
      setUniqueUsers(users);
    } catch (error: any) {
      console.error('Load unique users error:', error);
      // Don't show error message for this, just log it
    }
  }, []);

  // Load unique users on component mount
  useEffect(() => {
    loadUniqueUsers();
  }, [loadUniqueUsers]);



  // Memoized handlers to prevent unnecessary re-renders
  const handleView = useCallback((chatLog: ChatLog) => {
    setSelectedChatLog(chatLog);
    setViewDrawerVisible(true);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      await chatLogService.exportChatLogs('csv');
      message.success('Chat logs exported successfully');
    } catch (error: any) {
      message.error(error.message || 'Failed to export chat logs');
    }
  }, []);

  const handleDateRangeFilter = useCallback(async (dates: any, dateStrings: [string, string]) => {
    // This would require updating the chat log service to support date filtering
    message.info('Date range filtering will be implemented soon');
  }, []);

  // Memoized pagination handler
  const handlePaginationChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  }, [pageSize]);

  // Memoized search handler - Fixed to handle string value directly
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);



  // Memoized refresh handler with force reload
  const handleRefresh = useCallback(() => {
    loadChatLogs(true); // Force reload, bypass cache
  }, [loadChatLogs]);

  // Delete functionality handlers
  const handleDeleteByUserIds = useCallback(async () => {
    if (selectedUserIds.length === 0) {
      message.warning('Vui lòng chọn ít nhất một User ID để xóa');
      return;
    }

    confirm({
      title: 'Xác nhận xóa lịch sử chat',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Bạn có chắc chắn muốn xóa tất cả lịch sử chat của {selectedUserIds.length} user(s)?</p>
          <p><strong>User IDs:</strong> {selectedUserIds.join(', ')}</p>
          <p style={{ color: 'red' }}>Hành động này không thể hoàn tác!</p>
        </div>
      ),
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const result = await chatLogService.deleteChatLogsByUserIds(selectedUserIds);
          
          if (result.success) {
            message.success(`Đã xóa thành công ${result.total_deleted} tin nhắn của ${selectedUserIds.length} user(s)`);
            
            if (result.failed_deletions.length > 0) {
              message.warning(`Một số user không thể xóa: ${result.failed_deletions.map(f => f.user_id).join(', ')}`);
            }
            
            // Clear selection and reload data
            setSelectedUserIds([]);
            loadChatLogs(true);
          } else {
            throw new Error('Deletion was not completely successful');
          }
        } catch (error: any) {
          console.error('Delete error:', error);
          message.error(error.message || 'Có lỗi xảy ra khi xóa lịch sử chat');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  }, [selectedUserIds, loadChatLogs]);

  const handleUserIdSelectionChange = useCallback((values: string[]) => {
    setSelectedUserIds(values);
  }, []);

  // Handler for User ID filter
  const handleUserIdFilterChange = useCallback((value: string) => {
    setFilterUserId(value || undefined);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  // Clear all filters handler
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setFilterUserId(undefined);
    setCurrentPage(1);
  }, []);

  // Memoized utility functions to prevent re-computation
  const formatTimestamp = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  const truncateText = useCallback((text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }, []);

  // Memoized columns to prevent re-computation on every render
  const columns = useMemo(() => [
    {
      title: 'Session ID',
      dataIndex: 'session_id',
      key: 'session_id',
      width: 120,
      ellipsis: true,
      render: (sessionId: string) => (
        <Tag color="blue" style={{ fontSize: 11 }}>
          {sessionId ? sessionId.substring(0, 8) : 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'User Message',
      dataIndex: 'user_message',
      key: 'user_message',
      ellipsis: true,
      render: (text: string) => (
        <Text style={{ fontSize: 12 }}>
          {truncateText(text, 80)}
        </Text>
      ),
    },
    {
      title: 'Bot Response',
      dataIndex: 'bot_response',
      key: 'bot_response',
      ellipsis: true,
      render: (text: string) => (
        <Text style={{ fontSize: 12 }}>
          {truncateText(text, 80)}
        </Text>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) => (
        <Text style={{ fontSize: 11 }}>
          {formatTimestamp(timestamp)}
        </Text>
      ),
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      ellipsis: true,
      render: (userId: string) => userId || '-',
    },
  ], [truncateText, formatTimestamp]);

  return (
    <div>
      {/* Page Header */}
      <PageHeader
        title="Chat History"
        description="View and analyze chat conversation logs"
        icon={<MessageOutlined />}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export CSV
            </Button>
            <Popconfirm
              title="Xóa lịch sử chat"
              description={
                selectedUserIds.length === 0
                  ? "Vui lòng chọn User IDs trong phần filter trước"
                  : `Xóa lịch sử của ${selectedUserIds.length} user(s)?`
              }
              onConfirm={handleDeleteByUserIds}
              okText="Xóa"
              cancelText="Hủy"
              okType="danger"
              disabled={selectedUserIds.length === 0}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                disabled={selectedUserIds.length === 0}
                loading={deleteLoading}
              >
                Xóa theo User ID ({selectedUserIds.length})
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error Loading Data"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filters */}
      <FilterCard
        filters={[
          {
            type: 'input' as const,
            key: 'search',
            placeholder: 'Search messages...',
            value: searchText,
            onChange: handleSearchChange,
            span: 8
          },
          {
            type: 'select' as const,
            key: 'userIdFilter',
            placeholder: 'Filter by User ID',
            value: filterUserId,
            onChange: handleUserIdFilterChange,
            options: uniqueUsers.map(userId => ({ label: userId, value: userId })),
            span: 8
          },
          {
            type: 'dateRange' as const,
            key: 'dateRange',
            placeholder: 'Date Range',
            onChange: (value: any) => handleDateRangeFilter(value, value),
            span: 8
          },
          {
            type: 'custom' as const,
            key: 'userSelect',
            span: 24,
            customElement: (
              <Select
                mode="multiple"
                placeholder="Chọn User IDs để xóa..."
                value={selectedUserIds}
                onChange={handleUserIdSelectionChange}
                style={{ width: '300px' }}
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
                maxTagCount="responsive"
              >
                {uniqueUsers.map(userId => (
                  <Option key={userId} value={userId}>
                    {userId}
                  </Option>
                ))}
              </Select>
            )
          }
        ]}
        onClear={handleClearFilters}
        onRefresh={handleRefresh}
        loading={loading}
      />

      {/* Chat Logs Table */}
      <Card className="content-card">
        {initialLoading ? (
          <div className="loading-overlay">
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Loading chat history...</Text>
            </div>
          </div>
        ) : (
          <StandardTable
            dataSource={chatLogs}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePaginationChange,
              pageSizeOptions: ['10', '20', '50'],
            }}
            actions={{
              onView: handleView
            }}
            enableFixedHeader
            enableSort
          />
        )}
      </Card>

      {/* View Drawer */}
      <Drawer
        title="Chat Log Details"
        placement="right"
        onClose={() => setViewDrawerVisible(false)}
        open={viewDrawerVisible}
        width={600}
      >
        {selectedChatLog && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small" title="Session Information">
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Session ID:</Text>
                  <br />
                  <Text copyable style={{ fontSize: 12 }}>
                    {selectedChatLog.session_id || 'N/A'}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong>User ID:</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>
                    {selectedChatLog.user_id || 'N/A'}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text strong>Timestamp:</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>
                    {formatTimestamp(selectedChatLog.timestamp)}
                  </Text>
                </Col>
              </Row>
            </Card>

            <Card size="small" title="User Message">
              <TextArea
                value={selectedChatLog.user_message}
                rows={4}
                readOnly
                style={{ fontSize: 14 }}
              />
            </Card>

            <Card size="small" title="Bot Response">
              <TextArea
                value={selectedChatLog.bot_response}
                rows={6}
                readOnly
                style={{ fontSize: 14 }}
              />
            </Card>

            {selectedChatLog.metadata && Object.keys(selectedChatLog.metadata).length > 0 && (
              <Card size="small" title="Metadata">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4,
                  fontSize: 12,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(selectedChatLog.metadata, null, 2)}
                </pre>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default History;
